import { FormattedMessage } from 'react-intl';

import { Box, Typography, useTheme } from '@mui/material';

import _merge from 'lodash/merge';

import { CompanyKpis, CustomerCount } from '@arrived/models';

import { CompanyKpiDisplay } from '../CompanyKpiDisplay';
import { PerformanceTable } from './PerformanceTable';

import { Square, Triangle } from '../images';

export interface PerformancePageProps {
  customerCount: CustomerCount;
  kpis: CompanyKpis;
}

export const PerformancePage = ({ customerCount, kpis }: PerformancePageProps) => {
  const { palette } = useTheme();

  return (
    <Box>
      <Box
        position="relative"
        px={{ xs: 4, md: 12 }}
        py={{ xs: 8, md: 12 }}
        sx={{ backgroundColor: 'white', overflowX: 'hidden' }}
      >
        <Triangle
          color={palette.background.iceGray}
          style={{
            position: 'absolute',
            top: 0,
            right: '90px',
            transformOrigin: 'top',
            transform: 'rotate(-135deg) scale(3)',
          }}
        />
        <Square
          color={palette.mint[500]}
          style={{
            position: 'absolute',
            top: '70px',
            right: '15px',
            transformOrigin: 'center',
            transform: 'rotate(45deg) scale(0.2)',
          }}
        />
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'flex-end' }}
          gap={{ xs: 2, lg: 8 }}
          position="relative"
          maxWidth="1240px"
          width="100%"
          my={0}
          mx="auto"
        >
          <Box display="flex" alignItems="flex-start" flexDirection="column" flex="1 1 50%" gap={2.5} height="100%">
            <Typography
              variant="h5.bold"
              textTransform="uppercase"
              sx={{
                // Backup in case gradient text not supported
                color: 'primary',
                background: ({ gradients }) => gradients.sea.horizontal,
                '-webkit-background-clip': 'text',
                '-webkit-text-fill-color': 'transparent',
                '-moz-background-clip': 'text',
                '-moz-text-fill-color': 'transparent',
              }}
            >
              <FormattedMessage id="historical-performance" defaultMessage="Historical Performance" />
            </Typography>
            <Typography variant="h2.semibold">
              <FormattedMessage id="performance-header" defaultMessage="Delivering On Client Investments" />
            </Typography>
          </Box>
          <Box flex="1 1 50%">
            <Box borderLeft={({ palette }) => `2px solid ${palette.tertiary[500]}`} pl={2}>
              <Typography color="text.800" variant="body1">
                <FormattedMessage
                  id="performance-description"
                  defaultMessage="How have we delivered on client returns? Below, you'll find the current performance of all our properties, hand-picked by our investment team. Rental home returns are obtained both through rental income and property value appreciation."
                />
                <br />
                <br />
                <FormattedMessage
                  id="performance-description-2"
                  defaultMessage="So go ahead - be inspired by The Lierly or emboldened by The Salem. Arrived aims to provide optimal returns for every client's investment goals."
                />
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <CompanyKpiDisplay
        kpis={_merge(kpis, customerCount)}
        kpi1="property-values"
        kpi2="active-markets"
        kpi3="properties-funded"
        align="left"
        maxWidth="unset"
      />
      <Box
        display="flex"
        flexDirection="column"
        gap={{ xs: 1, md: 2 }}
        px={{ xs: 0, md: 8, lg: 12 }}
        pt={{ xs: 4, md: 12 }}
        pb={{ xs: 0, md: 12 }}
        sx={{ backgroundColor: 'background.iceGray' }}
      >
        <Box
          maxWidth="1240px"
          width="100%"
          my={0}
          mx="auto"
          textAlign={{ xs: 'left', md: 'right' }}
          pr={{ xs: 2, md: 4 }}
          pl={{ xs: 2, md: 0 }}
        >
          <Typography variant="body1" color="text.800">
            <FormattedMessage
              id="performance-table-context"
              defaultMessage="The following values were last updated January 2023."
            />
          </Typography>
        </Box>
        <Box
          maxWidth="1240px"
          width="100%"
          my={0}
          mx="auto"
          px={{ xs: 0, md: 2 }}
          pt={2}
          sx={{ backgroundColor: 'white' }}
        >
          <PerformanceTable />
        </Box>
      </Box>
    </Box>
  );
};
